import { useEffect, useRef, useMemo } from 'react';

const hexToRgb = (hex) => {
  // Robust hex parsing
  const h = hex.replace('#', '');
  const fullHex = h.length === 3 ? h.split('').map(s => s + s).join('') : h.padEnd(6, '0');
  const r = parseInt(fullHex.slice(0, 2), 16);
  const g = parseInt(fullHex.slice(2, 4), 16);
  const b = parseInt(fullHex.slice(4, 6), 16);
  return { r, g, b };
};

const SpotlightCard = ({ 
  children, 
  className = '', 
  tailwindColor = "#E67000", 
  alpha = 0.2,
  radius = 400 // Customizable radius
}) => {
  const divRef = useRef(null);
  
  // Memoize color to prevent recalculation on every render
  const { r, g, b } = useMemo(() => hexToRgb(tailwindColor), [tailwindColor]);

  useEffect(() => {
    const el = divRef.current;
    if (!el) return;

    const updatePosition = (clientX, clientY) => {
      const rect = el.getBoundingClientRect();
      el.style.setProperty('--mouse-x', `${clientX - rect.left}px`);
      el.style.setProperty('--mouse-y', `${clientY - rect.top}px`);
      el.style.setProperty('--opacity', '1');
    };

    const handleMouseMove = (e) => updatePosition(e.clientX, e.clientY);
    
    const handleTouchMove = (e) => {
      if (e.touches[0]) {
        updatePosition(e.touches[0].clientX, e.touches[0].clientY);
      }
    };

    const handleLeave = () => el.style.setProperty('--opacity', '0');

    el.addEventListener('mousemove', handleMouseMove);
    el.addEventListener('mouseleave', handleLeave);
    el.addEventListener('touchmove', handleTouchMove, { passive: true });
    el.addEventListener('touchend', handleLeave);

    return () => {
      el.removeEventListener('mousemove', handleMouseMove);
      el.removeEventListener('mouseleave', handleLeave);
      el.removeEventListener('touchmove', handleTouchMove);
      el.removeEventListener('touchend', handleLeave);
    };
  }, []);

  return (
    <div 
      ref={divRef} 
      className={`relative overflow-hidden group rounded-xl border border-white/10 bg-black/5 ${className}`}
      style={{
        '--mouse-x': '-100%',
        '--mouse-y': '-100%',
        '--opacity': '0'
      }}
    >
      {/* Spotlight Overlay above background, blended so text remains readable */}
      <div
        className="absolute inset-0 pointer-events-none transition-opacity duration-300 z-20 mix-blend-soft-light"
        style={{
          opacity: 'var(--opacity)',
          background: `radial-gradient(${radius}px circle at var(--mouse-x) var(--mouse-y), rgba(${r}, ${g}, ${b}, ${alpha}), transparent 80%)`,
        }}
      />
      
      {/* Content Container */}
      <div className="relative z-10 h-full">{children}</div>
    </div>
  );
};

export default SpotlightCard;